import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { api, imgSrc } from "@/lib/api";
import { useSeo } from "@/lib/site";
import { Reveal, MaskLines } from "@/components/Reveal";

const SWATCH = { Porcelain: "#E6E1D8", Pewter: "#8F8F8B", Pottery: "#9E5A3C", Nickel: "#4B4B49" };

export default function BlockDetail() {
  const { slug } = useParams();
  const [block, setBlock] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [colour, setColour] = useState("Porcelain");

  useSeo(block ? `${block.name} Breeze Block — Breeze Craft Sydney` : "Block — Breeze Craft",
    block ? `${block.name}: ${block.dimensions}, ${block.finish}. Supplied and installed Sydney-wide by Breeze Craft.` : "");

  useEffect(() => {
    setBlock(null);
    setNotFound(false);
    setColour("Porcelain");
    api.get(`/blocks/${slug}`).then((r) => setBlock(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24" data-testid="block-not-found">
        <p className="text-neutral-600">That block isn't in the catalogue.</p>
        <Link to="/catalogue" className="mt-4 inline-flex items-center gap-2 text-terracotta text-sm font-medium"><ArrowLeft size={16} /> Back to catalogue</Link>
      </div>
    );
  }
  if (!block) return <div className="max-w-7xl mx-auto px-4 py-24 text-sm text-neutral-500" data-testid="block-loading">Loading…</div>;

  const colourImages = block.colour_images || {};
  const colours = block.colours.filter((c) => colourImages[c]);
  const mainImage = colourImages[colour] || block.images[0];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14 sm:py-20" data-testid="block-detail-page">
      <Link to="/catalogue" data-testid="block-back-link" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-terracotta transition-colors">
        <ArrowLeft size={16} /> Catalogue
      </Link>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <Reveal className="relative aspect-square overflow-hidden bg-bone-dark">
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.img
                key={mainImage}
                src={imgSrc(mainImage)}
                alt={`${block.name} breeze block in ${colour}`}
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="block-main-image"
              />
            </AnimatePresence>
            <div className="absolute bottom-0 left-0 bg-bone px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-soft" data-testid="block-colour-label">
              {block.name} — {colour}
            </div>
          </Reveal>
          {colours.length > 1 && (
            <div className="mt-5" data-testid="block-colour-picker">
              <p className="kicker">Colour</p>
              <div className="mt-3 flex flex-wrap gap-3">
                {colours.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`block-colour-${c.toLowerCase()}`}
                    aria-pressed={c === colour}
                    onClick={() => setColour(c)}
                    className={`group flex items-center gap-2.5 border px-3 py-2 text-xs transition-colors ${c === colour ? "border-ink bg-ink text-bone" : "border-ink/15 text-ink hover:border-ink/50"}`}
                  >
                    <span className="w-4 h-4 rounded-full border border-black/10 shrink-0" style={{ backgroundColor: SWATCH[c] }} />
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div>
          <p className="kicker">{block.brand}</p>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl font-medium tracking-tight text-ink" data-testid="block-name">
            <MaskLines lines={[block.name]} />
          </h1>
          <Reveal delay={0.15}>
            <p className="mt-5 text-neutral-600 leading-relaxed">{block.description}</p>
          </Reveal>
          <Reveal delay={0.25} className="mt-8 border-y border-ink/10 divide-y divide-ink/10">
            <div className="py-4 flex justify-between gap-6">
              <span className="kicker">Dimensions</span>
              <span className="font-mono text-sm text-ink text-right" data-testid="block-dimensions">{block.dimensions}</span>
            </div>
            <div className="py-4 flex justify-between gap-6">
              <span className="kicker">Finish</span>
              <span className="text-sm text-ink text-right">{block.finish}</span>
            </div>
            <div className="py-4 flex justify-between gap-6">
              <span className="kicker">Colours</span>
              <span className="text-sm text-ink text-right">{block.colours.join(" · ")}</span>
            </div>
            <div className="py-4 flex justify-between gap-6">
              <span className="kicker">Typical uses</span>
              <span className="text-sm text-ink text-right">{block.typical_uses.join(" · ")}</span>
            </div>
            <div className="py-4 flex justify-between gap-6">
              <span className="kicker">Spec</span>
              <span className="font-mono text-sm text-ink text-right">Exposure Grade · non-structural screen block</span>
            </div>
          </Reveal>
          <Reveal delay={0.35} className="mt-8 flex flex-wrap gap-4">
            <Link
              to={`/quote?block=${block.slug}&colour=${colour}`}
              data-testid="block-quote-button"
              className="inline-flex items-center gap-2 bg-terracotta text-white px-7 py-3.5 text-sm font-semibold hover:bg-terracotta-dark transition-colors"
            >
              Get a quote on {block.name} in {colour} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
